<script setup>
import { defineProps, ref, computed } from 'vue';
import { Modal, Popover, AppButton, RadioButton } from '@n11/so-ui-components';
import { OFFER_STATUS, DEAL_TYPES } from '@/utils/enum/offerStatus';
import CurrencyInput from '@/components/DataTable/CurrencyInput';
import { modifyDeal, applyDeal, rejectOffer } from '../services/productDealService';
import { useStore } from 'vuex';

const acceptOfferModalRef = ref(null);
const revokeModalRef = ref(null);
const acceptNewOfferModalRef = ref(null);
const umbrellaCampaignModalRef = ref(null);
const popoverRef = ref(null);
const newSalesPrice = ref(null);
const errorText = ref();
const isLoading = ref(false);
const store = useStore();
const selectedOffer = ref('');
const currencyInput = ref(null);
const umbrellaCampaignInfo = ref({ title: '', message: '' });

const props = defineProps({
  // Same type with OfferStatus. Because it is determined according to offers.
  dealStatus: {
    type: String,
    required: false,
    default: OFFER_STATUS.WAITING_FOR_ACCEPTANCE,
  },
  dealType: {
    type: String,
    required: false,
    default: DEAL_TYPES.DEAL,
  },
  startDate: {
    type: Date,
    required: false,
  },
  commission: {
    type: Number,
    required: false,
  },
  price: {
    type: Number,
    required: false,
  },
  dealId: {
    type: Number,
    required: false,
  },
  groupId: {
    type: Number,
    required: false,
  },
  categoryId: {
    type: Number,
    required: false,
  },
  productId: {
    type: Number,
    required: false,
  },
  commissionRates: {
    type: Object,
    required: false,
  },
  dealStatuses: {
    type: Object,
    required: false,
  },
  hasVariants: {
    type: Boolean,
    required: false,
  },
  index: {
    type: Number,
    required: false,
  },
});

const offerTitles = {
  advantageousOffer: 'Avantajlı Teklif',
  superOffer: 'Süper Teklif',
  shockingOffer: 'Kaçmaz Teklif'
};

const dynamicPosition = computed(() => {
  if (props.index > 5) {
    return 'top-center';
  }

  return 'bottom-center';
})

const selectOffer = (offer) => {
  const rates = props.commissionRates[offer];
  if (!rates) return;

  selectedOffer.value = offer;
  
  newSalesPrice.value = (offer === 'shockingOffer') 
    ? rates.upperPrice 
    : rates.lowerPrice;
  currencyInput.value?.setValue(newSalesPrice.value);
}

const resetOfferPopup = () => {
  newSalesPrice.value = null;
  selectedOffer.value = '';
  errorText.value = '';
}

const currencyInputOptions = { 
  locale: 'tr-TR',
  currency: 'TRY',
  precision: 2,
  currencyDisplay: 'symbol',
  hideCurrencySymbolOnFocus: false,
  hideGroupingSeparatorOnFocus: false,
  hideNegligibleDecimalDigitsOnFocus: false,
  autoDecimalDigits: false,
  useGrouping: true,
  accountingSign: false,
  valueRange: { min: 0 },
};

const getOfferTitle = (key) => offerTitles[key];
const formatPrice = (price) => {
  return price.toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
  });
};

const getbuttonText = () => {
  if (
    (
      props.dealStatus === OFFER_STATUS.WAITING_FOR_ACCEPTANCE ||
      props.dealStatus === OFFER_STATUS.REFUSED
    ) && props.dealType !== DEAL_TYPES.NEW_DEAL
  ) {
    return 'Teklif Seç';
  }

  return 'Yeni Teklif Seç';
};

const addNewOffer = () => {
  if (popoverRef.value) {
    popoverRef.value.popoverToogle(); 
  }

  const isAllWaitingForAcceptance = Object.values(props.dealStatuses).every(
    value => value === undefined || value === "WAITING_FOR_ACCEPTANCE"
  );

  if (isAllWaitingForAcceptance) {
    acceptOfferModalRef.value.openModal();
  } else {
    acceptNewOfferModalRef.value.openModal();
  }
};

const handleContinue = () => {
  const offer = selectedOffer.value;
  if (!offer) {
    errorText.value = `Bir teklif seçmelisiniz.`;
    return;
  }

  const { upperPrice, lowerPrice } = props.commissionRates[offer];
  if (newSalesPrice.value > upperPrice) {
    errorText.value = `Bu teklif için fiyat en fazla ₺${upperPrice} olmalıdır.`;
  } else if (newSalesPrice.value < lowerPrice) {
    errorText.value = `Bu teklif için fiyat en az ₺${lowerPrice} olmalıdır.`;
  } else {
    errorText.value = '';
    addNewOffer();
  }
}

const handleModifyDeal = async () => {
  isLoading.value = true;

  try {
    await modifyDeal({
      dealId: props.dealId,
      groupId: props.groupId,
      price: newSalesPrice.value,
      categoryId: props.categoryId,
      productId: props.productId,
    });

    store.commit("common/TRIGGER_DEAL_TABLE_REFRESH", Date.now());
  } catch {
    acceptNewOfferModalRef.value.hiddenModal();
  }
  isLoading.value = false;
}

const handleRejectOffer = async () => {
  isLoading.value = true;
  
  try {
    await rejectOffer({
      dealId: props.dealId,
      groupId: props.groupId,
    });

    store.commit("common/TRIGGER_DEAL_TABLE_REFRESH", Date.now());
  } catch {
    isLoading.value = false;
    revokeModalRef.value.hiddenModal();
  }
}

const handleApplyDeal = async () => {
  isLoading.value = true;
  try {
    await applyDeal({
      dealId: props.dealId,
      groupId: props.groupId,
      price: newSalesPrice.value,
      productId: props.productId,
    })
    store.commit("common/SET_TOASTER_DATA", {
      type: 'success',
      title: '',
      description: 'Seçtiğiniz teklif başarıyla uygulandı.',
    });

    store.commit("common/TRIGGER_DEAL_TABLE_REFRESH", Date.now());
  } catch (error) {
    isLoading.value = false;
    acceptOfferModalRef.value.hiddenModal();

    if (error.data?.errors?.[0]?.code === "PRODUCT_DEAL.umbrellaCampaignActive") {
      umbrellaCampaignInfo.value = {
        title: error.data?.errors?.[0]?.title || '',
        message: error.data?.errors?.[0]?.message || '',
      };
      umbrellaCampaignModalRef.value.openModal();
    } else if (error.data?.errors?.[0]?.code === "PRODUCT_DEAL.activeOfferExists") {
      store.commit("common/SET_TOASTER_DATA", {
        type: 'info',
        description: error.data?.errors?.[0]?.message,
      });
    } else if (error.data?.errors?.[0]?.code === "PRODUCT_DEAL.dealSpecialCommissionError") {
      store.commit("common/SET_TOASTER_DATA", {
        type: 'info',
        description: error.data?.errors?.[0]?.message,
      });
    } else {
      if (error.data?.errors?.[0]?.message) {
        store.commit("common/SET_TOASTER_DATA", {
          type: 'info',
          description: error.data?.errors?.[0]?.message,
        });
      } else {
        store.commit("common/SET_TOASTER_DATA", {
          type: 'info',
          description: "Teknik bir hata oluştu.",
        });
      }
    }
  }
  isLoading.value = false;
}
const newCommissionRate = () => {
  const offers = ['advantageousOffer', 'superOffer', 'shockingOffer'];
  for (const offer of offers) {
    if (props.commissionRates[offer]) {
      const { upperPrice, lowerPrice, commission } = props.commissionRates[offer];
      if (upperPrice >= newSalesPrice.value && newSalesPrice.value >= lowerPrice) {
        return commission;
      }
    }
  }
  return props.commission;
};

const handleRevokeClick = () => {
  if (popoverRef.value?.showPopover) {
    popoverRef.value.popoverToogle();
  }
  revokeModalRef.value.openModal();
};
</script>

<template>
  <div class="seller-price-offer" v-if="!props.commission">
    <span class="not-exists">-</span>
    <p class="not-exists">Teklif bulunmuyor.</p>
  </div>
  <div class="seller-price-offer" v-else>
    <p class="text deleted-deal" v-if="props.dealType == DEAL_TYPES.BO_DELETED_DEAL">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="6" fill="#D13333"/>
        <path d="M4 6L8 6" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      n11 Tarafından Sonlandırıldı
    </p>
    <p class="text updated-deal" v-else-if="props.dealType === DEAL_TYPES.NEW_DEAL">
      <svg width="13" height="14" viewBox="0 0 13 14" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.15833 0.196826C6.96337 0.068838 6.73467 0 6.49992 0L6.38317 0.00569108C6.15121 0.0283645 5.92994 0.118241 5.74719 0.265508L4.66751 1.13629L3.28416 1.06576L3.16718 1.06561C2.93422 1.07663 2.70893 1.1552 2.51913 1.29293L2.42809 1.36602C2.25386 1.52042 2.1277 1.72285 2.06633 1.94909L1.70418 3.28601L0.543434 4.0442L0.448838 4.11282C0.267128 4.2586 0.131285 4.45439 0.0588149 4.6771L0.0281683 4.78961C-0.021955 5.01666 -0.00509705 5.25432 0.0780352 5.47328L0.571965 6.76886L0.413503 7.18528L0.389554 7.26088C0.305872 7.59028 0.480702 7.93734 0.806163 8.06136C0.832954 8.07156 0.88645 8.10848 0.962508 8.16809L3.91613 4.42202C4.53387 3.63856 5.6908 3.59715 6.36583 4.29477L6.44445 4.38203L6.48621 4.43531L6.57266 4.55721L9.41289 9.02869C9.92372 9.83291 9.63494 10.8881 8.76497 11.3542L8.6578 11.4072L8.52327 11.4566L6.58283 12.0627L6.58879 12.1249C6.59527 12.2383 6.59478 12.3523 6.58719 12.4662L6.5705 12.637L6.54309 12.8071C6.51172 12.9695 6.46611 13.1275 6.40705 13.2797C6.65006 13.4689 6.99977 13.4739 7.25008 13.2724L8.32904 12.4035L9.71412 12.4725L9.83087 12.4727C10.0635 12.4617 10.2891 12.383 10.4791 12.2449L10.5702 12.1717C10.7445 12.0169 10.8706 11.814 10.9316 11.5873L11.2951 10.2504L12.4556 9.49622L12.5505 9.42743C12.7328 9.28132 12.8688 9.08533 12.9413 8.86238L12.9719 8.74975C13.022 8.52246 13.005 8.28457 12.9216 8.06547L12.428 6.77022L12.9215 5.47573L12.9577 5.36468C13.019 5.14003 13.0137 4.90163 12.9412 4.67873L12.8997 4.56964C12.8065 4.35638 12.6528 4.17401 12.4564 4.04587L11.2958 3.28872L10.9336 1.95107L10.8975 1.83993C10.815 1.62212 10.6705 1.43234 10.4807 1.29461L10.383 1.23071C10.1821 1.11294 9.95048 1.05571 9.71623 1.06741L8.33176 1.13561L7.25288 0.265704L7.15833 0.196826Z" fill="#6CB9F0"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.165846 10.9097L4.78424 5.0522C5.04504 4.72143 5.55461 4.74736 5.78044 5.10289L8.62067 9.57439C8.83616 9.91366 8.67085 10.3649 8.2871 10.4848L5.39652 11.3886C5.61563 11.7565 5.69233 12.1926 5.61089 12.6141C5.52629 13.0519 5.27743 13.4409 4.90667 13.7075C4.72299 13.8348 4.51519 13.9234 4.29604 13.9676C4.07688 14.0118 3.851 14.0108 3.63226 13.9646C3.41351 13.9184 3.20652 13.828 3.02399 13.699C2.87797 13.5957 2.75007 13.4695 2.65002 13.3332L2.58036 13.2293L2.13859 12.5509C2.11268 12.5111 2.09199 12.4695 2.07637 12.4267L1.32976 12.6601C1.06584 12.7426 0.779354 12.6378 0.631159 12.4045L0.129883 11.6152C-0.00917904 11.3962 0.00523892 11.1134 0.165846 10.9097ZM3.27269 12.0526L3.61454 12.578C3.6442 12.6266 3.6835 12.6686 3.73 12.7015C3.77651 12.7344 3.82925 12.7574 3.88498 12.7692C3.94072 12.7809 3.99827 12.7812 4.05411 12.7699C4.10995 12.7587 4.1629 12.7361 4.20113 12.7097C4.31014 12.6313 4.38505 12.5142 4.41051 12.3824C4.43089 12.277 4.41838 12.1685 4.38244 12.0815L4.35124 12.0206L4.26904 11.9016C4.23873 11.8578 4.21495 11.8113 4.19748 11.7635L3.27269 12.0526Z" fill="#2B86D1"/>
      </svg>
      Teklif Yenilendi
    </p>
    <p class="text">Mevcut satış fiyatı ve komisyon:</p>
    <p class="price">{{ formatPrice(props.price) }}</p>
    <p class="commission">%{{ props.commission }} Komisyon</p>
    <div class="popover-wrapper" v-if="props.dealType != DEAL_TYPES.BO_DELETED_DEAL">
      <Popover
        ref="popoverRef"
        :position="dynamicPosition"
        color="#ffffff"
        :withOverlay="true"
        :zIndex="10001"
        to="body"
      >
        <template v-slot:content="{ on }">
          <AppButton
            v-on="on"
            :title="getbuttonText()"
            primary
            sm
            @click="resetOfferPopup"
            class="popover-wrapper__btn mt-3"
          />
          <AppButton
            v-if="(dealStatus !== OFFER_STATUS.WAITING_FOR_ACCEPTANCE && dealStatus !== OFFER_STATUS.REFUSED) || (dealType === DEAL_TYPES.NEW_DEAL && dealStatus !== OFFER_STATUS.REFUSED)"
            :title="'Tekliften Vazgeç'"
            secondary
            sm
            class="popover-wrapper__btn"
            @click="handleRevokeClick"
          />
        </template>

        <template #popoverContent>
          <div class="popover-wrapper__content">
            <div class="popover-wrapper__content__header">
              <p class="popover-wrapper__content__title">Teklif Seçin</p>
              <div v-if="hasVariants">
                <p class="all-variants-badge">Tüm Varyantlarda Geçerli</p>
              </div>
              <div v-else>
                <p class="all-variants-badge">Tek Varyantta Geçerli</p>
              </div>
            </div>
            <div class="popover-wrapper__current">
              <p class="current-title">Mevcut satış fiyatı ve komisyon:</p>
              <div class="current-details">
                <p class="current-details__price">{{ formatPrice(props.price) }}</p>
                <p class="current-details__text">%{{ props.commission }} Komisyon</p>
              </div>
            </div>
            <div class="popover-wrapper__list">
              <div 
                v-for="(offer,key) in  props.commissionRates" 
                :key="key" 
                class="offer-item"
              >
                <RadioButton
                  name="offer"
                  :preSelected="selectedOffer === key"
                  @select="selectOffer(key)"
                  :isInputFull="true"
                >
                  <div class="offer-item__content">
                    <span class="offer-item__title">{{ getOfferTitle(key) }}</span>
                    <div class="current-values">
                      <p class="current-values__price">
                        {{ formatPrice(offer.upperPrice) }} 
                        <template v-if="offer.lowerPrice > 0"> - {{ formatPrice(offer.lowerPrice) }}</template>
                        <template v-else> ve altı</template>
                      </p>
                      <p class="commission">%{{ offer.commission }} Komisyon</p>
                    </div>
                  </div>
                </RadioButton>
              </div>
            </div>
            <CurrencyInput
              ref="currencyInput"
              v-model="newSalesPrice"
              class="popover-wrapper__content__input"
              :options="currencyInputOptions"
              :class="[{ error: errorText }]"
              :key="`currency-input-${key}`"
            />
            <p v-if="errorText" class="popover-wrapper__content__error-text">{{ errorText }}</p>
            <AppButton
              title="Devam Et"
              md
              primary
              @click="handleContinue"
            />
          </div>
        </template>
      </Popover>
    </div>

    <Modal
      ref="revokeModalRef"
      title="Tekliften vazgeçmek istediğinize emin misiniz?"
      negativeActionText="Geri Dön"
      positiveActionText="Tekliften Vazgeç"
      :isPositiveActionButtonDisabled="isLoading"
      :isPositiveActionPending="isLoading"
      @onPositiveActionClick="handleRejectOffer"
      @onNegativeActionClick="$refs.revokeModalRef.hiddenModal()"
    >
      <template v-slot:body>
        <div class="accept-body">
          <p v-if="hasVariants">
            Komisyon teklifi <span class="accept-body__bold">ürünün tüm varyantları</span> 
            için iptal edilecek.
          </p>
          <p v-else>
            Komisyon teklifi <span class="accept-body__bold">ürünün tek varyantı</span> 
            için iptal edilecek.
          </p>
          <div class="description">
            Geçerlilik tarihleri süresince bu teklife tekrar katılabilirsiniz.
          </div>
        </div>
      </template>
    </Modal>

    <Modal
      ref="acceptOfferModalRef"
      type="success"
      title="Seçiminizi onaylıyor musunuz?"
      negativeActionText="Geri Dön"
      positiveActionText="Onayla"
      :isPositiveActionButtonDisabled="isLoading"
      :isPositiveActionPending="isLoading"
      @onPositiveActionClick="handleApplyDeal"
      @onNegativeActionClick="acceptOfferModalRef.hiddenModal()"
    >
      <template v-slot:body>
        <div class="accept-body">
          <p v-if="hasVariants">
            Onaylarsanız seçtiğiniz tarih aralığında aşağıdaki yeni fiyat ve komisyon oranı, ürünün 
            <span class="accept-body__bold">tüm varyantlarında</span> 
            geçerli olacaktır.
          </p>
          <p v-else>
            Onaylarsanız seçtiğiniz tarih aralığında aşağıdaki yeni fiyat ve komisyon oranı geçerli olacaktır.
          </p>
          <div class="change-offer">
            <div class="change-offer__old">
              <span class="title">Mevcut Fiyat & Komisyon</span>
              <span class="price">{{ formatPrice(price) }}</span>
              <span class="commission">%{{ commission }} Komisyon</span>
            </div>
            <div class="change-offer-new">
              <span class="title">Yeni Teklif</span>
              <span class="price">{{ formatPrice(newSalesPrice) }}</span>
              <span class="commission">%{{ newCommissionRate() }} Komisyon</span>
            </div>
          </div>
          <div class="description">
            Daha sonra istediğiniz zaman değişiklik yapabilir veya tekliften vazgeçebilirsiniz.
          </div>
        </div>
      </template>
    </Modal>

    <Modal
      ref="acceptNewOfferModalRef"
      type="info"
      title="Bu teklife geçiş yapmayı onaylıyor musunuz?"
      negativeActionText="Geri Dön"
      positiveActionText="Onayla"
      :isPositiveActionButtonDisabled="isLoading"
      :isPositiveActionPending="isLoading"
      @onPositiveActionClick="handleModifyDeal"
      @onNegativeActionClick="acceptNewOfferModalRef.hiddenModal()"
    >
      <template v-slot:body>
        <div class="accept-body">
          <p v-if="hasVariants">Onaylarsanız seçtiğiniz tarih aralığında aşağıdaki yeni fiyat ve komisyon oranı, ürünün <span class="accept-body__bold">tüm varyantlarında</span> geçerli olacaktır.</p>
          <p v-else>
            Onaylarsanız belirtilen tarihlerde geçiş yapacağınız teklif geçerli
            olacak.
          </p>
          <div class="change-offer">
            <div class="change-offer__old">
              <span class="title">Mevcut Fiyat & Komisyon</span>
              <span class="price">{{ formatPrice(price) }}</span>
              <span class="commission">%{{ commission }} Komisyon</span>
            </div>
            <div class="change-offer-new">
              <span class="title">Yeni Teklif</span>
              <span class="price">{{ formatPrice(newSalesPrice) }}</span>
              <span class="commission">%{{ newCommissionRate() }} Komisyon</span>
            </div>
          </div>
          <div class="description">
            Daha sonra istediğiniz zaman değişiklik yapabilir veya tekliften vazgeçebilirsiniz.
          </div>
        </div>
      </template>
    </Modal>

    <Modal
      ref="umbrellaCampaignModalRef"
      type="info"
      :title="umbrellaCampaignInfo.title"
      positiveActionText="Anladım"
      @onPositiveActionClick="umbrellaCampaignModalRef.hiddenModal()"
    >
      <template v-slot:body>
        <p class="umbrella-campaign-message">{{ umbrellaCampaignInfo.message }}</p>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.seller-price-offer {
  height: 100%;
  padding: 16px 12px;

  span {
    &.not-exists {
      color: $colorN60;
      font-size: 13px;
      font-weight: 600;
      line-height: 18px;
    }
  }

  p {
    &.not-exists {
      color: $colorN60;
      font-size: 10px;
      font-weight: 600;
      line-height: 12px;
    }

    &.text {
      color: $colorD50;
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
      margin-bottom: 4px;
      margin-bottom: 4px;
    }

    &.price {
      color: $colorN80;
      font-size: 13px;
      font-weight: 700;
      line-height: 18px;
      margin-bottom: 4px;
    }

    &.commission {
      border: 1px solid $colorN30;
      border-radius: 4px;
      color: $colorN60;
      font-size: 11px;
      font-weight: 600;
      line-height: 14px;
      padding: 2px 6px;
      display: inline-flex;
      background-color: $colorN00;
    }

    &.deleted-deal {
      display: flex;
      padding: 3px 8px;
      align-items: center;
      gap: 4px;
      border-radius: 4px;
      border: 1px solid $colorE20;
      background: $colorN00;
      color: $colorE60;
      font-size: 11px;
      font-weight: 600;
      line-height: 14px;
      width: fit-content;
    }

    &.updated-deal {
      display: flex;
      padding: 3px 8px;
      align-items: center;
      gap: 4px;
      width: 118px;
      color: $colorB60;
      border-radius: 4px;
      border: 1px solid $colorB20;
      font-size: 11px;
      font-weight: 600;
      line-height: 14px;
    }
  }

  .umbrella-campaign-message {
    color: $colorN80;
    text-align: center;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.05px;
  }

  .accept-body {
    gap: 16px;
    display: flex;
    flex-direction: column;

    &__bold {
      font-weight: 700 !important;
    }

    p {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }

    .change-offer {
      display: flex;
      padding-top: 10px;
      align-items: center;
      gap: 20px;
      align-self: stretch;

      &__old {
        background-color: $colorD05;
        &::after {
          content: '';
          width: 24px;
          height: 24px;
          background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgaWQ9ImFycm93X3dhcm1fdXBfMjRkcF81RjYzNjhfRklMTDBfd2dodDQwMF9HUkFEMF9vcHN6MjQgNiI+CjxnIGlkPSJWZWN0b3IiPgo8cGF0aCBkPSJNNS42IDhIOS4yVjE2SDUuNlY4Wk0yIDhINC40VjE2SDJWOFoiIGZpbGw9IiNEM0Q2REYiLz4KPHBhdGggZD0iTTEwLjQgOEgxN1YxNkgxMC40VjhaIiBmaWxsPSIjRDNENkRGIi8+CjwvZz4KPHBhdGggaWQ9IlBvbHlnb24gMSIgZD0iTTIyIDEyTDEzLjc1IDE5Ljc5NDJMMTMuNzUgNC4yMDU3N0wyMiAxMloiIGZpbGw9IiNEM0Q2REYiLz4KPC9nPgo8L3N2Zz4K");
          position: absolute;
          right: -22px;
          z-index: 9;
        }
      }

      > div {
        position: relative;
        display: flex;
        padding: 24px 24px 16px 24px;
        flex-direction: column;
        flex: 1 0 0;
        align-self: stretch;
        border-radius: 6px;
        border: 1px solid $colorD10;
        align-items: center;

        .title {
          width: max-content;
          display: flex;
          gap: 2px;
          padding: 4px 8px;
          justify-content: center;
          align-items: center;
          position: absolute;
          left: 50%;
          -webkit-transform: translateX(-50%);
          transform: translateX(-50%);
          top: -9px;
          border-radius: 6px;
          border: 0.5px solid $colorD20;
          background: $colorN00;
          color: $colorD50;
          font-size: 10px;
          font-weight: 600;
          line-height: 12px;
        }

        .price {
          color: var(--Neutral---Background-N80, #242424);
          font-size: 16px;
          font-weight: 700;
          line-height: 20px;
          letter-spacing: -0.05px;
        }

        .commission {
          display: flex;
          height: 18px;
          padding: 2px 4px;
          align-items: center;
          gap: 4px;
          border-radius: 4px;
          border: 1px solid $colorD20;
          background: $colorN00;
          color: $colorD50;
          text-align: center;
          font-size: 10px;
          font-weight: 600;
          line-height: 12px;
          margin-top: 4px;
          flex-shrink: 0;
        }

        &.change-offer-new {
          border: 1px solid $colorP20;
          background: $colorM05;

          .title {
            color: white;
            background: $colorM50;
            line-height: 10px;
          }

          .price {
            color: $colorM50;
          }

          .commission {
            border: 1px solid $colorP20;
            color: $colorM50;
          }
        }
      }
    }

    .description {
      gap: 8px;
      display: flex;
      padding: 16px;
      align-items: flex-start;
      border-radius: 6px;
      font-size: 13px;
      line-height: 18px;
      letter-spacing: -0.05px;
      text-align: start;
      background: $colorB05;
      color: $colorN80;

      &::before {
        content: "";
        width: 24px;
        min-width: 24px;
        height: 24px;
        display: block;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:v='https://vecta.io/nano'%3E%3Ccircle cx='12' cy='12' r='10' fill='%234fa4e5'/%3E%3Cpath d='M12 11a1 1 0 0 1 .993.884L13 12v4a1 1 0 0 1-1.993.116L11 16v-4a1 1 0 0 1 1-1zm0-4a1 1 0 1 1 0 2 1 1 0 1 1 0-2z' fill='%23fff'/%3E%3C/svg%3E");
      }
    }
  }

  .seller-price-offer :deep(.modal-wrapper .modal-container .modal-body .content) {
    gap: 16px;
  }

  .offerInformation {
    gap: 16px !important;
    flex-direction: column;

    &__title {
      color: $colorN80;
      text-align: center;
      font-size: 20px;
      font-weight: 700;
      line-height: 26px;
      letter-spacing: -0.2px;
      margin-bottom: 16px;
    }

    &__description {
      color: $colorN80;
      text-align: center;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: -0.05px;
    }
  }

  &__current {
    display: flex;
    flex-direction: column; 
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 8px;

    .current-details {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
}

.popover-text.is-teleported {
  padding: 0 !important;
  &.bottom-center {
    top: calc(var(--anchor-top) + var(--anchor-height) + 40px) !important;
    left: calc(var(--anchor-left) + (var(--anchor-width) / 2) + 20px) !important;
    transform: translate(-50%, 0) !important;
    bottom: unset !important;
  }

  &.top-center {
    top: calc(var(--anchor-top) - 8px) !important;
    left: calc(var(--anchor-left) + (var(--anchor-width) / 2)) !important;
    transform: translate(-50%, -100%) !important;
    bottom: unset !important;
  }

  .current {
    &-values {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 4px;
      margin-top: 1px;

      .commission {
        flex-shrink: 0;
      }

      &__price {
        color: $colorN80;
        font-size: 12px;
        line-height: 16px;
        font-weight: 400;
        flex-shrink: 0;
        display: flex;
      }
    }

    &-details {
      display: flex;
      align-items: center;
      gap: 4px;

      &__price {
        color: $colorN80;
        font-size: 13px;
        font-weight: 700;
        line-height: 18px;
      }

      &__text {
        color: $colorN60;
        font-size: 11px;
        line-height: 14px;
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 4px;
        border: 1px solid $colorN30;
        background: $colorN00;
      }
    }

    &-title {
      color: $colorN60;
      font-size: 11px;
      line-height: 14px;
      text-align: left;
      margin-bottom: 4px;
    }
  }

  .commission-badge,
  .commission {
    font-size: 11px;
    font-weight: 600;
    color: $colorD50 !important;     
    padding: 2px 6px;    
    border: 1px solid $colorD20;
    border-radius: 4px;
    background-color: $colorN00;
    line-height: 14px;
  }

  .all-variants {
    &-badge  {
      color: $colorB50 !important;
      font-size: 10px;
      font-weight: 600;
      line-height: 12px;
      padding: 3px 6px;
      border: 1px solid $colorB30;
      border-radius: 4px;
      background-color: $colorN00;
      white-space: nowrap;
      width: fit-content;
    }

    &-description {
      font-size: 16px;
      font-weight: 400;
      line-height: 30px;
      color: #4E5C78;
      text-align: left;
      margin-top: 4px;
      letter-spacing: -0.05px;
      font-style: normal;
    }

    &__bold {
      font-weight: 700 !important;
    }
  } 

  .popover-wrapper__list {
    display: flex;
    flex-direction: column;
    gap: 8px;

    :deep(.radioButton-container) {
      width: 100%;

      .radioButton {
        flex-shrink: 0;
      }
    }

    .offer-item {
      &__content {
        display: flex;
        flex-direction: column;
        text-align: left;
        width: 100%;
      }

      &__title {
        color: $colorN60;  
        font-size: 11px;
        line-height: 14px;
      }

      &__details {
        display: flex;
        align-items: center;
        gap: 8px;

        .price {
          font-size: 14px;
          font-weight: 700;
          color: $colorN80;
        }
      }
    }
  }
}

.popover-wrapper {
  .popover {
    width: 100%;
  }

  .mt-3 {
    margin-top: 12px !important;
  }

  &__btn {
    margin-top: 8px;
    font-size: 11px !important;
    height: 14px !important;
  }

  &__content {
    border-radius: 8px;
    border: 1px solid var(--Dusk---Decorative-D40, $colorD40);
    box-shadow: 0 0 0 3px $colorD20;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-width: 284px;
    padding: 12px;
    background-color: $colorN00;
    &__header{
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__title {
      color: $colorN80;
      font-size: 13px;
      font-style: normal;
      font-weight: 700;
      line-height: 18px;
      text-align: start;
      letter-spacing: -0.05px;
    }

    &__error-text {
      color: $colorE50;
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
      text-align: left;
      margin-top: -4px;
    }

    &__input {
      &.error {
        border: 1px solid $colorE50;
        outline: 3px solid $colorE10;
      }
    }
  }
}
</style>
<style lang="scss">
.popover-text {
  &.is-teleported {
    padding: 0 !important;
  }
}
</style>
